import { useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { deletePartner, getPartnerById, getPartners, handleCredit, newPartner, updatePartner } from "../../services/partners-service";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
  VStack,
  FormControl,
  FormLabel,
  Input as ChakraInput,
  Center,
  Spinner,
} from "@chakra-ui/react";

import { Table, TableRowActions, Panel, PhoneInput, PageHeading } from "@bemap/ui";
import { CreditPayload, ITeam } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../reset-password/reset-password-service";

const PartnersPage: React.FC = () => {
  const toast = useToast()
  const { t } = useTranslation()
  const initialPartner: ITeam = {
    id: 0,
    name: '',
    userId: 0,
    personalTeam: false,
    isPremium: false,
    type: "partner",
    profilePhotoPath: "",
    user: {
      id: 0,
      name: '',
      email: '',
      phone: '',
      password: '',
      totalCredits: 0,
    }
  }

  const initialCredit: CreditPayload = {
    amount: 0,
    by_user_id: 0
  }

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete" | "transfer" | "retrieve" | "reset_password">();
  const [partners, setPartners] = useState<ITeam[]>([]);
  const [credits, setCredits] = useState<CreditPayload>(initialCredit);
  const [partner, setPartner] = useState<ITeam>(initialPartner);
  const [updateData, setUpdateData] = useState<string>("");

  const columns = useMemo<ColumnDef<ITeam, any>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: "Time",
        header: t('admin.partner_table_team'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row?.user?.name,
        id: "Nome do Partner",
        header: "PARTNER",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row?.user?.email,
        id: "Email",
        header: "EMAIL",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row?.user?.phone,
        id: "Telefone",
        header: t('admin.partner_table_phone'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.createdAt,
        id: "Criado em",
        header: t('admin.partner_table_created'),
        cell: (info) => {
          const date = info.getValue();
          return date ? new Date(date).toLocaleDateString() : '';
        },
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.totalCredits,
        id: "Créditos",
        header: t('admin.partner_table_credits'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.id,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row) => {
          return (
            <TableRowActions
              editAction={() => editPartner(row.getValue())}
              deleteAction={() => openDeleteDialog(row.getValue())}
              addAction={() => openTransferModal(row.getValue())}
              retrieveAction={() => openRetrieveModal(row.getValue())}
              resetAction={() => openResetPasswordDialog(row.getValue())}
            />
          );
        },
      },
    ],
    [t]
  );

  useEffect(() => {
    loadPartners();
  }, [updateData]);

  function closeModal() {
    onClose()
    setPartner(initialPartner)
    setCredits(initialCredit)
  }

  function openTransferModal(teamId: number) {
    setCredits({
      ...credits,
      to_team_id: teamId
    })
    onOpen()
    setModalType('transfer')
  }

  function openRetrieveModal(teamId: number) {
    setCredits({
      ...credits,
      from_team_id: teamId,
      to_team_id: 0
    })
    onOpen()
    setModalType('retrieve')
  }

  async function loadPartners() {
    setIsLoading(true);
    try {
      const response = await getPartners();
      setPartners(response.message);
      setIsLoading(false);
    } catch (error) {
      setPartners([]);
      setIsLoading(false);
    }
  }

  async function editPartner(partnerId: number) {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const req = await getPartnerById(partnerId);

      toast.closeAll()
      setPartner(req.message);
      setIsLoading(false);
      setModalType("edit");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function openDeleteDialog(partnerId: number) {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const req = await getPartnerById(partnerId);

      toast.closeAll()
      setPartner(req.message);
      setModalType("delete");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function openResetPasswordDialog(partnerId: number) {
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      })
      const req = await getPartnerById(partnerId);
      toast.closeAll()
      setPartner(req.message);
      setModalType("reset_password");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function createNewPartner() {
    setIsLoading(true);
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await newPartner(partner)
      toast.closeAll()
      toast({
        title: t('admin.partner_register_successfully'),
        status: 'success',
        isClosable: true,
      })
      setIsLoading(false)
      setIsSaving(false)
      closeModal()
      setUpdateData(new Date().toLocaleString())
    } catch (error: any) {
      toast.closeAll();
      toast({
        title: error.message,
        status: 'error',
        isClosable: true,
      })
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  async function handleCreditTransaction() {
    setIsSaving(true);
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await handleCredit(credits, modalType as string)
      toast.closeAll()
      toast({
        title: t('admin.partner_credits_sucessfully'),
        status: 'success',
        isClosable: true,
      })
      setIsSaving(false)
      closeModal()
      setUpdateData(new Date().toLocaleString())
    } catch (error: any) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
      setIsSaving(false);
    }
  }

  const handleUpdatePartner = async (payload: ITeam) => {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await updatePartner(payload);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleDeletePartner = async () => {
    toast({
      title: t('general.deleting'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await deletePartner(partner.id as number);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error: any) {
      toast.closeAll()
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  const handleResetPassword = async () => {
    try {
      setIsSaving(true)
      await resetPassword(partner.userId);
      setUpdateData(new Date().toLocaleString())
      setIsSaving(false)
      closeModal()
    } catch (error: any) {
      toast.closeAll()
      setIsSaving(false)
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  async function handleChange(event: any) {
    setPartner({
      ...partner,
      [event.target.name]: event.target.value,
    });
  }

  async function handleChangeCredit(event: any) {
    setCredits({
      ...credits,
      [event.target.name]: event.target.value,
    });
  }

  useEffect(() => {
    if (partner?.name?.trim() && (partner?.email?.trim() || partner?.user?.email?.trim()) && (partner?.phone?.trim() || partner?.user?.phone?.trim())) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [partner])

  function RenderModal() {
    switch (modalType) {
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('admin.partner_edit_title')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t('admin.partner_register_name')}</FormLabel>
                    <ChakraInput
                      name="name"
                      placeholder={t('general.placeholder_name')}
                      type="text"
                      onChange={handleChange}
                      value={partner?.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>E-mail</FormLabel>
                    <ChakraInput
                      name="email"
                      placeholder={t('general.placeholder_name')}
                      type="email"
                      onChange={(e) => setPartner({ ...partner, user: { ...partner?.user, email: e.target.value } })}
                      value={partner?.user?.email || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('admin.partner_table_phone')}</FormLabel>
                    <PhoneInput
                      value={partner?.user?.phone || ""}
                      onChange={(phone: string) => setPartner({ ...partner, user: { ...partner?.user, phone } })}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={() => handleUpdatePartner(partner)} isLoading={isSaving}
                  isDisabled={!isValid}
                >
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('admin.partner_register_title')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t('admin.partner_register_name')}</FormLabel>
                    <ChakraInput
                      name="name"
                      placeholder={t('general.placeholder_name')}
                      type="text"
                      onChange={handleChange}
                      value={partner?.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>E-mail</FormLabel>
                    <ChakraInput
                      name="email"
                      placeholder={t('general.placeholder_email')}
                      type="email"
                      onChange={handleChange}
                      value={partner?.email || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('admin.partner_table_phone')}</FormLabel>
                    <ChakraInput
                      name="phone"
                      placeholder="Phone"
                      type="tel"
                      onChange={(e) => setPartner({ ...partner, user: { ...partner?.user, phone: e.target.value } })}
                      value={partner?.user?.phone || ""}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewPartner} isLoading={isSaving} isDisabled={!isValid}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('admin.partner_delete_title')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeletePartner} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
      case 'reset_password':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('auth.forgot_password_title_reset')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('auth.user_reset_password')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button isLoading={isSaving} colorScheme='red' onClick={handleResetPassword} ml={3}>
                    {t('auth.user_reset_password_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
      case "transfer":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('admin.partner_table_add_credits')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t("actions.add_credits_title")}</FormLabel>
                    <ChakraInput
                      name="amount"
                      placeholder="Ex: 200"
                      type="number"
                      min="0"
                      onChange={handleChangeCredit}
                      value={credits?.amount || ""}
                      required
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleCreditTransaction} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "retrieve":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('actions.retrieve_credits')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t("actions.retrieve_credits_title")}</FormLabel>
                    <ChakraInput
                      name="amount"
                      placeholder="Ex: 100"
                      type="number"
                      min="0"
                      onChange={handleChangeCredit}
                      value={credits?.amount || ""}
                      required
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleCreditTransaction} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap="20px" w="100%" align="stretch">
        <PageHeading title={'Partners'} actions={
          [
            {
              label: t('admin.partner_register_title'),
              icon: <AddIcon />,
              onClick: () => {
                onOpen();
                setModalType("new");
              }
            }
          ]
        } />
        <Panel>
          <Table
            tableId="admin-partners-table"
            columns={columns}
            data={partners}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default PartnersPage; 